import React, { useState, useEffect } from 'react';
import { FaSearch, FaFilter, FaTimes, FaCalendarAlt, FaClock, FaCheckCircle, FaExclamationTriangle, FaHourglassHalf } from 'react-icons/fa';
import { fetchQueryLogs } from '../../services/service';
import { useAsgardeoAuth } from '../../hooks/useAsgardeoAuth';

const QueryLogList = ({ onSelect, filters, onFilterChange, onClearFilters }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState(null);

  const auth = useAsgardeoAuth();

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchQueryLogs({
        page: currentPage,
        limit: filters.limit,
        from: filters.fromDate,
        to: filters.toDate,
        authContext: auth
      });

      setLogs(response.logs || []);
      setTotalPages(response.totalPages || 1);
      setTotalLogs(response.total || 0);
    } catch (err) {
      console.error('Error loading query logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [currentPage, filters, auth]);

  const handleLogClick = (log) => {
    setSelectedLogId(log._id || log.id);
    onSelect(log);
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'high_confidence':
        return <FaCheckCircle className="text-green-500" />;
      case 'medium_confidence':
        return <FaHourglassHalf className="text-yellow-500" />;
      case 'low_confidence':
      case 'very_low_confidence':
        return <FaExclamationTriangle className="text-red-500" />;
      default:
        return <FaClock className="text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'high_confidence':
        return 'text-green-600 bg-green-100';
      case 'medium_confidence':
        return 'text-yellow-600 bg-yellow-100';
      case 'low_confidence':
        return 'text-orange-600 bg-orange-100';
      case 'very_low_confidence':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const parseQueryType = (queryType) => {
    if (!queryType) return 'Unknown';
    
    const typeMap = {
      'general': 'General Query',
      'technical': 'Technical Support',
      'billing': 'Billing Inquiry',
      'feature': 'Feature Request',
      'bug': 'Bug Report',
      'account': 'Account Issue',
      'other': 'Other'
    };
    
    return typeMap[queryType.toLowerCase()] || queryType;
  };

  if (loading && logs.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pl_prim_color_1"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <FaExclamationTriangle className="text-4xl mx-auto mb-2" />
            <p className="text-lg font-semibold">Error Loading Logs</p>
          </div>
          <p className="text-pl_txt_light_3 mb-4">{error}</p>
          <button
            onClick={loadLogs}
            className="px-4 py-2 bg-pl_prim_color_1 text-white rounded-lg hover:bg-pl_prim_color_2 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-pl_border_light_1 bg-pl_bg_light_5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-pl_txt_light_1 flex items-center gap-2">
            <FaSearch className="text-pl_prim_color_1" />
            Query Classification Logs
          </h2>
          <button
            onClick={() => setShowMobileFilter(!showMobileFilter)}
            className="lg:hidden p-2 text-pl_txt_light_2 hover:text-pl_prim_color_1 transition-colors"
          >
            <FaFilter className="w-4 h-4" />
          </button>
        </div>

      </div>

      {/* Logs List */}
      <div className="flex-1 overflow-y-auto">
        {logs.length === 0 ? (
          <div className="p-8 text-center">
            <FaSearch className="mx-auto text-4xl text-pl_txt_light_3 mb-4" />
            <p className="text-pl_txt_light_2 font-medium">No query logs found</p>
            <p className="text-sm text-pl_txt_light_3 mt-2">Try adjusting your filters or check back later</p>
          </div>
        ) : (
          <div className="divide-y divide-pl_border_light_1">
            {logs.map((log) => (
              <div
                key={log._id || log.id}
                onClick={() => handleLogClick(log)}
                className={`p-4 cursor-pointer hover:bg-pl_bg_light_4 transition-colors ${
                  selectedLogId === (log._id || log.id) ? 'bg-pl_bg_light_4 border-l-4 border-l-pl_prim_color_1' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {(() => {
                      // Calculate status based on confidence scores
                      let maxConfidence = 0;
                      if (log.response && typeof log.response === 'object') {
                        for (const [key, value] of Object.entries(log.response)) {
                          if (typeof value === 'number' && value > maxConfidence) {
                            maxConfidence = value;
                          }
                        }
                      } else if (log.confidence) {
                        maxConfidence = log.confidence;
                      }
                      
                      const getStatusFromConfidence = (confidence) => {
                        if (confidence >= 0.8) return 'high_confidence';
                        if (confidence >= 0.6) return 'medium_confidence';
                        if (confidence >= 0.4) return 'low_confidence';
                        return 'very_low_confidence';
                      };
                      
                      const status = getStatusFromConfidence(maxConfidence);
                      return (
                        <>
                          {getStatusIcon(status)}
                          <span className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(status)}`}>
                            {status.replace('_', ' ')}
                          </span>
                        </>
                      );
                    })()}
                  </div>
                  <div className="text-xs text-pl_txt_light_3 flex items-center gap-1">
                    <FaCalendarAlt className="w-3 h-3" />
                    {formatTimestamp(log.timestamp)}
                  </div>
                </div>

                <div className="space-y-1">
                  <div>
                    <h3 className="text-sm font-medium text-pl_txt_light_2">Original Query:</h3>
                    <p className="text-sm text-pl_txt_light_1 mt-1">
                      {truncateText(log.original_query || log.originalQuery || log.query || 'N/A')}
                    </p>
                  </div>
                  
                  {log.processed_query && (
                    <div>
                      <span className="text-xs font-medium text-pl_txt_light_2">Processed Query:</span>
                      <p className="text-xs text-pl_txt_light_1 mt-1">
                        {truncateText(log.processed_query)}
                      </p>
                    </div>
                  )}

                  {log.response && typeof log.response === 'object' ? (
                    <div>
                      <span className="text-xs font-medium text-pl_txt_light_2">Classifications:</span>
                      <div className="mt-1 space-y-1">
                        {Object.entries(log.response)
                          .filter(([key, value]) => typeof value === 'number')
                          .sort(([,a], [,b]) => b - a)
                          .slice(0, 3) // Show top 3 classifications
                          .map(([type, confidence], index) => (
                            <div key={index} className="flex items-center justify-between text-xs">
                              <span className="text-pl_txt_light_1">{type}</span>
                              <span className="text-pl_txt_light_2">{(confidence * 100).toFixed(1)}%</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  ) : log.classification_result && (
                    <div>
                      <span className="text-xs font-medium text-pl_txt_light_2">Classification:</span>
                      <p className="text-xs text-pl_txt_light_1 mt-1">
                        {truncateText(log.classification_result)}
                      </p>
                    </div>
                  )}
                </div>

                {(() => {
                  // Show confidence for the highest classification
                  let maxConfidence = 0;
                  if (log.response && typeof log.response === 'object') {
                    for (const [key, value] of Object.entries(log.response)) {
                      if (typeof value === 'number' && value > maxConfidence) {
                        maxConfidence = value;
                      }
                    }
                  } else if (log.confidence) {
                    maxConfidence = log.confidence;
                  }
                  
                  if (maxConfidence > 0) {
                    return (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs text-pl_txt_light_3 mb-1">
                          <span>Top Confidence</span>
                          <span>{(maxConfidence * 100).toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-pl_border_light_1 rounded-full h-1">
                          <div 
                            className="bg-gradient-to-r from-pl_prim_color_1 to-pl_prim_color_2 h-1 rounded-full"
                            style={{ width: `${maxConfidence * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-pl_border_light_1 bg-pl_bg_light_5">
          <div className="flex items-center justify-between">
            <div className="text-sm text-pl_txt_light_3">
              Showing {((currentPage - 1) * filters.limit) + 1} - {Math.min(currentPage * filters.limit, totalLogs)} of {totalLogs}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-pl_border_light_1 rounded hover:bg-pl_bg_light_4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-pl_txt_light_2">
                {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-pl_border_light_1 rounded hover:bg-pl_bg_light_4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QueryLogList; 